import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack,
  Divider,
  InputAdornment,
} from "@mui/material";
import { SectionLabel, PROVIDER_LABEL, WizardCard } from "./_wizardUi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  faGitlabSquare,
  faBitbucket,
  faGithub,
} from "@fortawesome/free-brands-svg-icons";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GitIcon from "@mui/icons-material/Folder";
import { useDispatch } from "react-redux";
import { useLoader } from "../../../../context/LoaderContext";
import { AppDispatch } from "../../../../store";

import {
  fetchBranches,
  fetchTags,
  registryAnalyze,
  registryProcess,
} from "../../../../features/slices/Registry";
import {
  addSuite,
  getSuiteById,
  updateSuite,
} from "../../../../features/slices/Suite";

import { useNotification } from "../../../../context/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import GitFrame from "./GitFrameForm";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { TreeViewBaseItem } from "@mui/x-tree-view/models";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";

import moment from "moment";
import GitResourceForm from "./GitResourceForm";
import {
  fetchSystem,
  postSystem,
} from "../../../../features/slices/SystemRegistry";
import { SuiteTagEditor } from "./SuiteTagEditor";
import SuiteReview from "./SuiteReview";
interface Suite {
  title: string;
  meta_data?: {
    agent: string;
    windows: string;
    agent_ver: string;
  };
  planed_start?: Date | string;
}

const suites_steps = [
  "Suite Title",
  "Select Framework",
  "Select Cases",
  "Resources",
  "Review",
];
const getItemDescendantsIds = (item: TreeViewBaseItem) => {
  if (!item.children) return [];

  const ids: string[] = [];
  item.children.forEach((child) => {
    ids.push(child.id);
    ids.push(...getItemDescendantsIds(child));
  });

  return ids;
};

const GitSuite = () => {
  const [gitUrl, setGitUrl] = useState("");
  const [authType, setAuthType] = useState("token");
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [sshFile, setSshFile] = useState<File | null>(null);
  const [sshPassphrase, setSshPassphrase] = useState("");
  const [hasPassphrase, setHasPassphrase] = useState(false);
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState({});
  const [iconType, setIconType] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [dropdownType, setDropdownType] = useState<string | null>("branches");
  const [fetchType, setFetchType] = useState("branches");
  const [activeStep, setActiveStep] = useState(0);
  const [suiteId, setSuiteId] = useState("");
  const [suite, setSuite] = useState<Suite>({ title: "" });
  const [treeItems, setTreeItems] = useState<TreeViewBaseItem[]>([]);
  const [selectedCases, setSelectedCases] = useState<string[]>([]);
  const [allCases, setAllCases] = useState<string[]>([]);

  const apiRef = useTreeViewApiRef();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();
  const { selectedId } = useParams();

  const validateUrl = (url: string) => {
    const githubPattern = /^(https:\/\/|git@)github\.com[:\/].*\.git$/;
    const gitlabPattern =
      /^(https:\/\/(?:[^@]+@)?gitlab\.com\/[\w\-]+\/[\w\-]+\.git|git@gitlab\.com:[\w\-]+\/[\w\-]+\.git)$/;
    const bitbucketPattern =
      /^(https:\/\/(?:[^@]+@)?bitbucket\.org\/[\w\-]+\/[\w\-]+\.git|git@bitbucket\.org:[\w\-]+\/[\w\-]+\.git)$/;
    const gitPattern = /^(https:\/\/|git@).*\.git$/;

    if (url.startsWith("git@")) {
      setAuthType("ssh_key");
      setIsValidUrl(true);
      setIconType("git");
      notify("SSH repository detected.", "success");
    } else if (githubPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("github");
      notify("GitHub repository detected.", "success");
    } else if (gitlabPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("gitlab");
      notify("GitLab repository detected.", "success");
    } else if (bitbucketPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("bitbucket");
      notify("Bitbucket repository detected.", "success");
    } else if (gitPattern.test(url)) {
      setAuthType("token");
      setIsValidUrl(true);
      setIconType("git");
      notify("Git repository detected.", "success");
    } else {
      setIsValidUrl(false);
      setIconType(null);
      notify("Invalid Git URL. Please enter a valid repository URL.", "error");
    }
  };

  const getSuite = async (id: string) => {
    try {
      const result = await dispatch(getSuiteById(id)).unwrap();
      if (result) {
        setSuite(result);
        setSuiteId(result.id);
        setActiveStep(1);
        loadCredentials("token");
        setAuthType("token");
        setIsValidUrl(true);
        notify("Suite fetched successfully", "success");
      } else {
        notify("Failed to fetch suite data", "error");
      }
      return result;
    } catch (error) {
      notify("An error occurred while fetching suite data", "error");
      console.error("Error fetching suite:", error);
    }
  };

  useEffect(() => {
    if (selectedId) {
      getSuite(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    switch (authType) {
      case "token":
        setIsFormValid(
          isValidUrl && username.trim() !== "" && token.trim() !== ""
        );
        break;
      case "ssh_key":
        setIsFormValid(isValidUrl && sshFile !== null);
        break;
      default:
        setIsFormValid(false);
        break;
    }
  }, [authType, gitUrl, username, token, sshFile, isValidUrl]);

  const handleNext = async () => {
    showLoader();
    try {
      const result = await dispatch(addSuite(suite));
      if (result.meta.requestStatus === "fulfilled") {
        hideLoader();
        setSuite(result.payload);
        setSuiteId(result.payload.id);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        notify("Suite created successfully", "success");
      }
    } catch (e) {
      hideLoader();
      notify("Failed to create suite", "error");
      console.error(e);
    }
  };
  const handleGitFrame = (data: any) => {
    if (data) {
      setActiveStep(2);
    }
  };
  const handleFileUpload = (event: any) => {
    setSshFile(event.target.files[0]);
  };
  const handleSshSubmit = async () => {
    if (!sshFile) {
      notify("Please upload an SSH key file.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("repo", gitUrl);
    const repoName = gitUrl.split("/").pop() || "";
    formData.append("title", repoName.replace(".git", ""));
    formData.append("reg_type", "ssh_key");
    formData.append("file", sshFile);
    const regValue = JSON.stringify({ token: sshPassphrase });
    if (hasPassphrase) {
      formData.append("reg_value", regValue);
    }

    try {
      const response = await dispatch(postSystem(formData)).unwrap();
      if (response.reg_value) {
        notify(
          "SSH key uploaded successfully and repository registered.",
          "success"
        );
        setIsFormValid(true);
      } else {
        throw new Error("Failed to register SSH key.");
      }
    } catch (error) {
      notify(
        "Failed to register repository with SSH key. Please try again.",
        "error"
      );
    }
  };

  useEffect(() => {
    if (authType && gitUrl) loadCredentials(authType);
  }, [authType, gitUrl]);

  const loadCredentials = async (type: string) => {
    const params = {
      reg_type: type,
      repo: gitUrl,
      mode: "first",
      entity_type: "cases",
    };
    const query = new URLSearchParams(params).toString();
    try {
      const response = await dispatch(fetchSystem(query)).unwrap();
      const cred = response;
      setSaveCredentials(!!cred);
      const regValue = cred.reg_value || {};
      setUsername(regValue.username || "");
      if (type === "token") {
        setToken(regValue.token || "");
      }
      setGitUrl(cred.repo);
      notify("Credentials loaded successfully", "success");
    } catch (error) {
      setSaveCredentials(false);
      notify("No credentials found for this repository.", "warning");
      console.error("Failed to load credentials:", error);
    }
  };

  const handleSaveCredentials = async () => {
    if (saveCredentials) {
      const formData = new FormData();
      formData.append("repo", gitUrl);
      const repoName = gitUrl.split("/").pop() || "";
      formData.append("title", repoName.replace(".git", ""));
      formData.append("reg_type", authType);
      formData.append("entity_type", "cases");

      const regValue = JSON.stringify({
        username,
        token:
          authType === "token"
            ? token
            : authType === "ssh_key"
            ? sshPassphrase
            : "",
      });

      formData.append("reg_value", regValue);

      if (authType === "ssh_key" && sshFile) {
        formData.append("file", sshFile);
      }

      try {
        await dispatch(postSystem(formData)).unwrap();
        notify("Credentials saved successfully", "success");
      } catch (error) {
        notify("Failed to save credentials", "error");
      }
    }
  };

  const fetchBranchesHandler = async () => {
    if (!isValidUrl || !isFormValid) {
      notify(
        "Please ensure all required fields are filled out correctly.",
        "error"
      );
      return;
    }
    setLoadingBranches(true);
    setDropdownType("branches");

    const requestData = {
      repo_url: gitUrl,
      username: username,
      password: authType === "token" ? token : "",
      token: authType === "token" ? token : "",
      reg_type: "git",
      suite_id: `${suiteId}`,
    };

    try {
      const response = await dispatch(fetchBranches(requestData)).unwrap();
      setBranches(response || []);
      setLoadingBranches(false);
      if (response.length > 0) {
        notify("Branches fetched successfully!", "success");
      } else {
        notify("No branches found in the repository.", "warning");
        setDropdownType(null);
      }
    } catch (error) {
      setLoadingBranches(false);
      notify(
        "Failed to fetch branches. Please check your credentials and try again.",
        "error"
      );
      setDropdownType(null);
    }
  };

  const fetchTagsHandler = async () => {
    if (!isValidUrl || !isFormValid) {
      notify(
        "Please ensure all required fields are filled out correctly.",
        "error"
      );
      return;
    }
    setLoadingTags(true);
    setDropdownType("tags");

    const requestData = {
      repo_url: gitUrl,
      username: authType === "token" ? username : "",
      password: authType === "token" ? token : "",
      token: authType === "token" ? token : "",
      reg_type: "git",
      suite_id: `${suiteId}`,
      // ssh_key: authType === "ssh_key" ? "" : "",
    };

    try {
      const response = await dispatch(fetchTags(requestData)).unwrap();
      setTags(response || []);
      setLoadingTags(false);
      if (response.length > 0) {
        notify("Tags fetched successfully!", "success");
      } else {
        notify("No tags found in the repository.", "warning");
        setDropdownType(null);
      }
    } catch (error) {
      setLoadingTags(false);
      notify(
        "Failed to fetch tags. Please check your credentials and try again.",
        "error"
      );
      setDropdownType(null);
    }
  };

  const analyzeBranch = async () => {
    setIsAnalyzing(true);

    const requestData = {
      repo_url: gitUrl,
      username: authType === "token" ? username : "",
      password: authType === "token" ? token : "",
      reg_type: "git",
      branch: branch,
      token: authType === "token" ? token : "",
      suite_id: `${suiteId}`,
    };

    try {
      const response = await dispatch(registryAnalyze(requestData)).unwrap();

      if (!response) {
        throw new Error("No response from the server.");
      }

      if (response.code === 404 || response.message) {
        notify(response.message, "error");
        setAnalyzeResult({});
      } else {
        setAnalyzeResult(response || {});
        await handleSaveCredentials();

        notify("Analysis completed successfully!", "success");
      }
    } catch (error: any) {
      const errorMessage =
        error.message ||
        "Failed to analyze branch. Please check your credentials and try again.";
      notify(errorMessage, "error");
    } finally {
      setIsAnalyzing(false);
    }
  };
  const handleReview = () => {
    if (selectedCases.length === 0) {
      notify("Please select at least one case to register.", "error");
      return;
    }
    if (analyzeResult && Object.keys(analyzeResult).length > 0) {
      setActiveStep(4);
    } else {
      notify("Please analyze the branch before proceeding.", "warning");
    }
  };
  const handleSubmit = async () => {
    showLoader();
    const unselectedCases = allCases.filter(
      (caseId) => !selectedCases.includes(caseId)
    );
    if (selectedCases.length === 0) {
      notify("Please select at least one case to register.", "error");
      hideLoader();
      return;
    }
    const requestData = {
      repo_url: gitUrl,
      username: authType === "token" ? username : "",
      password: authType === "token" ? token : "",
      branch: branch,
      reg_type: "git",
      token: "",
      suite_id: `${suiteId}`,
      unselectedCases,
    };

    dispatch(
      updateSuite({ id: suiteId, planed_start: suite.planed_start })
    ).unwrap();
    dispatch(registryProcess(requestData))
      .unwrap()
      .then(() => {
        hideLoader();
        notify("Repository registered successfully!", "success");
        navigate("/suites");
      })
      .catch((error) => {
        hideLoader();
        notify("Failed to register repository. Please try again.", "error");
        console.error(error);
      });
  };

  const getIcon = () => {
    switch (iconType) {
      case "github":
        return <FontAwesomeIcon icon={faGithub} size="2x" />;
      case "gitlab":
        return (
          <FontAwesomeIcon icon={faGitlabSquare} color="orange" size="2x" />
        );
      case "bitbucket":
        return <FontAwesomeIcon icon={faBitbucket} color="blue" size="2x" />;
      case "git":
        return <GitIcon color="action" />;
      default:
        return null;
    }
  };
  const countTotalCases = (analyzeResult = {}) => {
    return Object.values(analyzeResult).reduce((total: number, value: any) => {
      return total + countCasesInStructure(value.structure);
    }, 0);
  };

  const countCasesInStructure = (structure: any) => {
    if (!structure || typeof structure !== "object") return 0;

    let casesCount = 0;

    if (structure.cases) {
      casesCount += Object.keys(structure.cases).length;
    }

    for (const [key, value] of Object.entries(structure)) {
      if (key !== "cases" && typeof value === "object") {
        casesCount += countCasesInStructure(value);
      }
    }

    return casesCount;
  };

  const convertStructureToTreeViewItems = (
    structure: any,
    prefix = ""
  ): TreeViewBaseItem[] => {
    if (!structure || typeof structure !== "object") return [];

    const items = [];

    for (const [key, value] of Object.entries(structure)) {
      if (key === "tests" && typeof value === "object") {
        const testItems = value
          ? Object.entries(value)
              .filter(([testName, testDetails]) => {
                const resourceCount = (testDetails as any).cases
                  ? Object.keys((testDetails as any).cases).length
                  : 0;
                return resourceCount > 0;
              })
              .map(([testName, testDetails]) => {
                const resourceCount = (testDetails as any).cases
                  ? typeof testDetails === "object" &&
                    testDetails !== null &&
                    "cases" in testDetails
                    ? Object.keys((testDetails as any).cases).length
                    : 0
                  : 0;

                return {
                  id: `${prefix}-${testName}`,
                  label: `${testName} (${
                    resourceCount > 1
                      ? `${resourceCount} Configs`
                      : `${resourceCount} Config`
                  })`,
                  icon: <InsertDriveFileIcon />,
                  isLeaf: true,
                };
              })
          : [];

        items.push(...testItems);
      } else if (typeof value === "object") {
        items.push(
          ...convertStructureToTreeViewItems(value, `${prefix}-${key}`)
        );
      }
    }

    return items;
  };

  const getAllTestIds = (items: TreeViewBaseItem[]) => {
    const ids: string[] = [];
    const traverse = (item: TreeViewBaseItem) => {
      if ("isLeaf" in item && item.isLeaf) ids.push(item.id);
      if (item.children) item.children.forEach(traverse);
    };
    items.forEach(traverse);
    return ids;
  };

  useEffect(() => {
    if (analyzeResult && typeof analyzeResult === "object") {
      const items = Object.entries(analyzeResult).flatMap(([_, value]) =>
        convertStructureToTreeViewItems(value.structure)
      );
      setTreeItems(items);

      const allTestIds = getAllTestIds(items);
      setAllCases(allTestIds);
      setSelectedCases(allTestIds);
    }
  }, [analyzeResult]);

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent<Element, Event>,
    itemIds: string[]
  ) => {
    setSelectedCases(itemIds);
  };
  const countSelectedCasesByFramework = (
    framework: any,
    analyzeResult: any,
    selectedCases: any
  ) => {
    const tests = analyzeResult[framework]?.structure?.[framework]?.tests || {};

    let selectedCasesWithResources = 0;

    selectedCases.forEach((caseId: string) => {
      const [_, selectedFramework, testName] = caseId.split("-");

      if (selectedFramework === framework && tests[testName]?.cases) {
        selectedCasesWithResources += Object.keys(tests[testName].cases).length;
      }
    });

    return selectedCasesWithResources;
  };

  const renderAnalysisResult = () => {
    if (!analyzeResult || typeof analyzeResult !== "object") {
      return (
        <Typography variant="body1" sx={{ color: "red" }}>
          No analysis result available.
        </Typography>
      );
    }
    const handleUnselectByFramework = (framework: string) => {
      const newSelectedItems = selectedCases.filter(
        (caseId) => !caseId.startsWith(`-${framework}`)
      );
      setSelectedCases(newSelectedItems);
    };
    const handleSelectByFramework = (framework: string) => {
      const frameworkItems = treeItems.filter((item) =>
        item.id.startsWith(`-${framework}`)
      );

      const newSelectedItems = Array.from(
        new Set([...selectedCases, ...frameworkItems.map((item) => item.id)])
      );

      if (newSelectedItems.length === selectedCases.length) {
        notify("All cases are already selected.", "info");
      } else {
        setSelectedCases(newSelectedItems);
      }
    };

    const totalCases = countTotalCases(analyzeResult);

    return (
      <Box
        sx={{
          mt: 4,
          p: 2,
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
          Analysis Result (Total Cases: {totalCases})
        </Typography>
        {Object.entries(analyzeResult).map(([framework, value], index) => {
          const folderStructure = value.structure;
          const casesCount = countCasesInStructure(folderStructure);

          const selectedCasesCount = countSelectedCasesByFramework(
            framework,
            analyzeResult,
            selectedCases
          );

          return (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#e0e0e0" }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FolderIcon sx={{ mr: 1, color: "action.active" }} />
                  {framework} (Selected Cases: {selectedCasesCount}/{casesCount}
                  )
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    fontWeight: "bold",
                    color: "#333",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Path: {value?.abs || "N/A"}
                </Typography>
                {/* <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  Structure:
                </Typography> */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSelectByFramework(framework)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleUnselectByFramework(framework)}
                  >
                    Unselect All
                  </Button>
                </Box>
                <Box sx={{ ml: 2 }}>
                  {folderStructure ? (
                    <RichTreeView
                      defaultExpandedItems={[
                        "-pytest",
                        "-robot",
                        "-unittest",
                        "-sil",
                      ]}
                      items={convertStructureToTreeViewItems(value.structure)}
                      multiSelect
                      checkboxSelection
                      selectedItems={selectedCases}
                      onSelectedItemsChange={handleSelectedItemsChange}
                      apiRef={apiRef}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: "gray" }}>
                      No structure available.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };
  const handleDateChange = (newDate: moment.Moment | null) => {
    if (newDate) {
      setSuite({
        ...suite,
        planed_start: newDate.toISOString(),
      });
    }
  };
  const silItems = selectedCases.some((item) => item.startsWith("-sil"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, md: 3 },
        mx: "auto",
        maxWidth: 960,
        width: "100%",
      }}
    >
      <Box>
        <Typography
          component="h2"
          sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, lineHeight: 1.2 }}
        >
          Link Git repositories
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          Create the suite, connect the framework repository, then select case folders.
        </Typography>
      </Box>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          overflowX: "auto",
          pb: 0.5,
          "& .MuiStepLabel-label": {
            fontSize: { xs: 12, sm: 13 },
            mt: 0.75,
          },
          "& .MuiStepConnector-line": {
            borderColor: "divider",
          },
        }}
      >
        {suites_steps.map((label) => (
          <Step
            key={label}
            sx={{
              "& .MuiStepLabel-root .Mui-completed": {
                color: "#4caf50",
              },
            }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === 3 && (
        <WizardCard>
          <GitResourceForm
            suiteId={suiteId}
            goBack={() => setActiveStep(2)}
            onFinish={(resources) => {
              setActiveStep(4);
            }}
            silCases={silItems}
          />
        </WizardCard>
      )}
      {activeStep === 4 && (
        <WizardCard>
          <Stack spacing={3}>
            <SuiteReview
              suite={suite}
              gitUrl={gitUrl}
              branch={branch}
              analyzeResult={analyzeResult}
              selectedCases={selectedCases}
            />
            <SuiteTagEditor suiteId={suiteId} />
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={() => setActiveStep(3)}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Submit suite
              </Button>
            </Box>
          </Stack>
        </WizardCard>
      )}

      {activeStep === 0 && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 1,
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                Name the suite
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
                Use a short operational name that will be easy to find in the suites table.
              </Typography>
            </Box>
          <TextField
            label="Suite Title"
            fullWidth
            variant="outlined"
              placeholder="Example: Android nightly smoke"
            value={suite.title}
            onChange={(e) =>
              setSuite({
                title: e.target.value,
                meta_data: {
                  agent: "Windows",
                  windows: "true",
                  agent_ver: "0.00.01",
                },
              })
            }
          />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!suite.title}
                sx={{ minWidth: { xs: "100%", sm: 120 } }}
            >
              Next
            </Button>
          </Box>
          </Stack>
        </Paper>
      )}

      {activeStep === 1 && (
        <WizardCard>
          <GitFrame suiteId={suiteId} gitFrameData={handleGitFrame} />
        </WizardCard>
      )}
      {activeStep === 2 && (
        <WizardCard>
          <Stack spacing={3}>
            {/* ---- Cases repository ---- */}
            <Box>
              <SectionLabel>Cases repository</SectionLabel>
              <TextField
                autoComplete="off"
                label="Git cases repository URL"
                placeholder="https://github.com/org/tests.git"
                value={gitUrl}
                onChange={(e) => {
                  setGitUrl(e.target.value);
                  validateUrl(e.target.value);
                }}
                fullWidth
                error={Boolean(gitUrl) && !isValidUrl}
                helperText={
                  !gitUrl
                    ? "Repository that contains your test cases."
                    : isValidUrl
                    ? `Detected: ${
                        PROVIDER_LABEL[iconType ?? "git"] ?? "Git"
                      } repository`
                    : "That doesn't look like a valid .git URL."
                }
                InputProps={{
                  endAdornment: gitUrl ? (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {getIcon()}
                        {isValidUrl ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                      </Stack>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            {/* ---- Authentication ---- */}
            <Box
              sx={{
                opacity: isValidUrl ? 1 : 0.5,
                pointerEvents: isValidUrl ? "auto" : "none",
                transition: "opacity 150ms",
              }}
            >
              <SectionLabel>Authentication</SectionLabel>
              <Stack spacing={2}>
                <FormControl fullWidth disabled={!isValidUrl}>
                  <InputLabel>Authentication type</InputLabel>
                  <Select
                    value={authType}
                    label="Authentication type"
                    onChange={(e) => setAuthType(e.target.value)}
                  >
                    <MenuItem value="token">Token</MenuItem>
                    {/* <MenuItem value="ssh_key">SSH Key</MenuItem> */}
                  </Select>
                </FormControl>

                {authType === "token" && (
                  <>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        autoComplete="off"
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        autoComplete="off"
                        label="Access token"
                        type="password"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        fullWidth
                      />
                    </Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={saveCredentials}
                          onChange={(e) => setSaveCredentials(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Remember these credentials for this repository
                        </Typography>
                      }
                    />
                  </>
                )}

                {authType === "ssh_key" && (
                  <Stack spacing={1.5} alignItems="flex-start">
                    <Button variant="outlined" component="label">
                      Upload SSH key file
                      <input type="file" hidden onChange={handleFileUpload} />
                    </Button>
                    {sshFile && (
                      <Typography variant="body2" color="text.secondary">
                        {sshFile.name} ({(sshFile.size / 1024).toFixed(2)} KB)
                      </Typography>
                    )}
                    {sshFile && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={hasPassphrase}
                            onChange={(e) => setHasPassphrase(e.target.checked)}
                          />
                        }
                        label="This SSH key requires a passphrase"
                      />
                    )}
                    {hasPassphrase && (
                      <TextField
                        label="SSH key passphrase"
                        type="password"
                        value={sshPassphrase}
                        onChange={(e) => setSshPassphrase(e.target.value)}
                        fullWidth
                        helperText="Leave empty if your SSH key has no passphrase."
                      />
                    )}
                    <Button
                      variant="contained"
                      onClick={handleSshSubmit}
                      disabled={!sshFile}
                    >
                      Register SSH key
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* ---- Branch / tag ---- */}
            <Box
              sx={{
                opacity: isFormValid ? 1 : 0.5,
                pointerEvents: isFormValid ? "auto" : "none",
                transition: "opacity 150ms",
              }}
            >
              <SectionLabel>Branch / tag</SectionLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <FormControl sx={{ minWidth: 150 }} disabled={!isValidUrl}>
                  <InputLabel>Fetch</InputLabel>
                  <Select
                    value={fetchType}
                    label="Fetch"
                    onChange={(e) => setFetchType(e.target.value)}
                  >
                    <MenuItem value="branches">Branches</MenuItem>
                    <MenuItem value="tags">Tags</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={
                    fetchType === "branches"
                      ? fetchBranchesHandler
                      : fetchTagsHandler
                  }
                  disabled={!isFormValid || loadingBranches || loadingTags}
                  startIcon={
                    loadingBranches || loadingTags ? (
                      <CircularProgress size={18} />
                    ) : null
                  }
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {fetchType === "branches" ? "Fetch branches" : "Fetch tags"}
                </Button>
                {dropdownType === "branches" && branches.length > 0 && (
                  <FormControl sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      value={branch}
                      label="Branch"
                      onChange={(e) => setBranch(e.target.value)}
                    >
                      {branches.map((b) => (
                        <MenuItem key={b} value={b}>
                          {b}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {dropdownType === "tags" && tags.length > 0 && (
                  <FormControl sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>Tag</InputLabel>
                    <Select
                      value={branch}
                      label="Tag"
                      onChange={(e) => setBranch(e.target.value)}
                    >
                      {tags.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>

            {/* ---- Test cases ---- */}
            <Box
              sx={{
                opacity: branch ? 1 : 0.5,
                pointerEvents: branch ? "auto" : "none",
                transition: "opacity 150ms",
              }}
            >
              <SectionLabel>Test cases</SectionLabel>
              <Button
                variant="outlined"
                onClick={analyzeBranch}
                disabled={!branch || isAnalyzing}
                startIcon={isAnalyzing ? <CircularProgress size={18} /> : null}
              >
                Discover test cases
              </Button>
              {isAnalyzing && <LinearProgress sx={{ mt: 1.5 }} />}
              {Object.keys(analyzeResult).length > 0 && (
                <Box sx={{ mt: 2 }}>{renderAnalysisResult()}</Box>
              )}
            </Box>

            {/* ---- Advanced ---- */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid var(--app-border, #e4e7ec)",
                borderRadius: "10px !important",
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "var(--app-surface-muted, #f8fafc)" }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  Advanced settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    label="Planned start"
                    value={
                      suite.planed_start ? moment(suite.planed_start) : null
                    }
                    onChange={handleDateChange}
                    minDateTime={moment()}
                    slotProps={{
                      textField: { fullWidth: true, variant: "outlined" },
                    }}
                    sx={{ width: "100%" }}
                  />
                </LocalizationProvider>
              </AccordionDetails>
            </Accordion>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" onClick={() => setActiveStep(1)}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setActiveStep(3)}
                disabled={Object.keys(analyzeResult).length === 0}
              >
                Confirm selection
              </Button>
            </Box>
          </Stack>
        </WizardCard>
      )}
    </Box>
  );
};

export default GitSuite;
